/**
 * Leads kanban (F3-I). Columns = pipeline stages; draggable cards
 * (native drag & drop) that PATCH the stage on drop. Filters by source
 * and search, quick WhatsApp action, and a "new lead" dialog. Each card
 * links to the lead's detail.
 */
import { createSignal, For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { MessageCircle, Plus, Search } from "@fleet/icons";
import { buildWaLink } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { ServerDataGate } from "@/components/ui/server-data-gate";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { ExportButton } from "@/features/io/components/export-button";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { ALL_SOURCES, type Lead, type Stage, STAGES, useLeads, usePatchLead } from "./api";
import { sourceLabel, STAGE_LABEL } from "./labels";
import { NewLeadDialog } from "./new-lead-dialog";

const field =
  "rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

// TanStack's typed `to` is a strict union; a `string` value (like the shell's
// NAV) is accepted and avoids coupling the links to the generated union.
const leadHref = (id: string): string => `/crm/leads/${id}`;

export function CrmKanban() {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const [source, setSource] = createSignal<string>("");
  const [search, setSearch] = createSignal("");
  const [dialogOpen, setDialogOpen] = createSignal(false);
  const [dragging, setDragging] = createSignal<string | null>(null);

  const leads = useLeads(activeOrgId, () => ({
    source: source() || undefined,
    search: search().trim() || undefined,
  }));
  const patch = usePatchLead();

  const leadsByStage = (stage: Stage): Lead[] =>
    (leads.data ?? []).filter((l) => l.stage === stage);

  const moveTo = (leadId: string, stage: Stage) => {
    const lead = (leads.data ?? []).find((l) => l.id === leadId);
    if (!lead || lead.stage === stage) {
      return;
    }
    patch.mutate(
      { id: leadId, patch: { stage } },
      { onError: (err) => showToast(err.message, "error") },
    );
  };

  const waLink = (lead: Lead): string | null => {
    // An anonymized lead (its account was deleted) has neither name nor
    // phone: `buildWaLink` returns null with an empty phone and the
    // WhatsApp button disappears on its own, which is exactly what should happen.
    const name = lead.name ?? t(msg`Deleted account`);
    return buildWaLink(
      lead.phone ?? "",
      t(msg`Hi ${name}, thanks for your interest in Space Academy.`),
    );
  };

  return (
    <div>
      <div class="mb-4 flex flex-wrap items-center gap-2">
        <div class="relative">
          <Search class="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            class={`${field} pl-8`}
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            placeholder={t(msg`Search name, phone or email`)}
            data-testid="crm-search"
          />
        </div>
        <div class="w-48">
          <Select
            options={[
              { value: "", label: t(msg`All sources`) },
              ...ALL_SOURCES.map((s) => ({ value: s, label: t(sourceLabel(s)) })),
            ]}
            value={source()}
            onChange={setSource}
            aria-label={t(msg`Source`)}
          />
        </div>
        <div class="ml-auto flex items-center gap-2">
          {/* F4-B: export of leads (io.leads) with the active source filter. */}
          <ExportButton
            entityKey="io.leads"
            orgId={activeOrgId()}
            filters={{ fuente: source() || undefined }}
          />
          <Button onClick={() => setDialogOpen(true)} data-testid="crm-new-lead" class="gap-2">
            <Plus class="h-4 w-4" />
            {t(msg`New lead`)}
          </Button>
        </div>
      </div>

      {/* LF-B: the CRM is server-only — offline shows an honest state, not
          an eternal skeleton (query pending+paused with no network). */}
      <ServerDataGate
        pending={leads.isLoading}
        error={leads.isError}
        refetch={() => void leads.refetch()}
        skeleton={<Skeleton class="h-64 w-full rounded-xl" />}
      >
        <div class="flex gap-3 overflow-x-auto pb-3" data-testid="crm-kanban">
          <For each={STAGES}>
            {(stage) => (
              <div
                class="flex w-72 shrink-0 flex-col rounded-xl border border-border bg-muted/30 p-2"
                data-testid={`crm-column-${stage}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = dragging();
                  if (id) {
                    moveTo(id, stage);
                  }
                  setDragging(null);
                }}
              >
                <div class="mb-2 flex items-center justify-between px-1.5 py-1">
                  <span class="text-sm font-bold">{t(STAGE_LABEL[stage])}</span>
                  <span class="rounded-full bg-accent/15 px-2 text-xs font-semibold text-accent">
                    {leadsByStage(stage).length}
                  </span>
                </div>
                <div class="flex flex-col gap-2">
                  <For each={leadsByStage(stage)}>
                    {(lead) => (
                      <div
                        draggable="true"
                        onDragStart={() => setDragging(lead.id)}
                        onDragEnd={() => setDragging(null)}
                        class="group rounded-lg border border-border bg-card p-3 shadow-sm transition-shadow hover:shadow-md"
                        data-testid="crm-card"
                      >
                        <Link to={leadHref(lead.id)} class="block">
                          <div class="font-semibold text-foreground">
                            {lead.name ?? t(msg`Deleted account`)}
                          </div>
                          <div class="text-xs text-muted-foreground">{lead.phone}</div>
                          <Show when={lead.interest}>
                            <div class="mt-1 text-xs text-muted-foreground">{lead.interest}</div>
                          </Show>
                        </Link>
                        <div class="mt-2 flex items-center justify-between">
                          <span class="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                            {t(sourceLabel(lead.source))}
                          </span>
                          <Show when={waLink(lead)}>
                            {(href) => (
                              <a
                                href={href()}
                                target="_blank"
                                rel="noreferrer"
                                class="flex items-center gap-1 text-xs font-medium text-green-600 hover:underline"
                                data-testid="crm-card-wa"
                                aria-label={t(msg`Message on WhatsApp`)}
                              >
                                <MessageCircle class="h-3.5 w-3.5" />
                                {t(msg`Message`)}
                              </a>
                            )}
                          </Show>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>
      </ServerDataGate>

      <NewLeadDialog open={dialogOpen()} onOpenChange={setDialogOpen} org={activeOrgId()} />
    </div>
  );
}
